import React, { useEffect, useState } from "react";
import { Plus, Pencil, KeyRound, PauseCircle, Loader2, ShieldCheck, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtMonthYear = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};
const fmtRelative = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "Today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return fmtMonthYear(iso);
};

const initialsOf = (s) =>
  `${(s?.first_name || "").charAt(0)}${(s?.last_name || "").charAt(0)}`.toUpperCase() || "?";

const randomPin = (len) => {
  const digits = "0123456789";
  const buf = crypto.getRandomValues(new Uint8Array(len));
  return Array.from(buf, (b) => digits[b % 10]).join("");
};

// --------------- Create / Edit Staff modal ---------------
const StaffModal = ({ open, onClose, initial, onSaved }) => {
  const isEdit = !!(initial && initial.id);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName(initial?.first_name || "");
      setLastName(initial?.last_name || "");
      setEmail(initial?.email || "");
      setPin("");
      setErrors({});
      setSubmitting(false);
    }
  }, [open, initial]);

  const validate = () => {
    const e = {};
    if (!firstName.trim() || firstName.length > 50) e.first_name = "1-50 characters";
    if (!lastName.trim() || lastName.length > 50) e.last_name = "1-50 characters";
    if (!email.trim() || !/^.+@.+\..+$/.test(email.trim())) e.email = "Valid email required";
    if (!isEdit && !/^\d{4}$/.test(pin)) e.pin = "PIN must be exactly 4 digits";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/staff/${initial.id}`, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
        });
        toast.success(`Updated ${firstName} ${lastName}.`);
      } else {
        await api.post("/admin/staff", {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          pin,
        });
        toast.success(`Added ${firstName}. Share PIN ${pin} securely.`);
      }
      onSaved();
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(code === "email_taken" ? "Email already in use." : "Couldn't save staff.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="staff-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            {isEdit ? `Edit ${initial.first_name}` : "Add staff"}
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            {isEdit
              ? "Update profile fields. To change the PIN, use the Reset PIN button on the row."
              : "Adds a 4-digit PIN-based staff account. They'll sign in at /staff/login."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
                First name
              </span>
              <input
                data-testid="staff-first-name"
                type="text"
                value={firstName}
                maxLength={50}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
              />
              {errors.first_name && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.first_name}</p>}
            </label>
            <label className="block">
              <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
                Last name
              </span>
              <input
                data-testid="staff-last-name"
                type="text"
                value={lastName}
                maxLength={50}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
              />
              {errors.last_name && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.last_name}</p>}
            </label>
          </div>

          <label className="block">
            <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
              Email
            </span>
            <input
              data-testid="staff-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-body text-[14px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
            {errors.email && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.email}</p>}
          </label>

          {!isEdit && (
            <label className="block">
              <span className="block font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft mb-1.5">
                4-digit PIN
              </span>
              <div className="flex gap-2">
                <input
                  data-testid="staff-pin"
                  type="text"
                  inputMode="numeric"
                  value={pin}
                  maxLength={4}
                  onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                  placeholder="0000"
                  className="flex-1 bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-mono text-[18px] tracking-[0.4em] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
                />
                <button
                  type="button"
                  data-testid="staff-pin-random"
                  onClick={() => setPin(randomPin(4))}
                  className="px-3 py-2.5 border border-napoli-gold/40 bg-napoli-cream-warm text-napoli-espresso font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:border-napoli-oxblood/40 inline-flex items-center gap-1.5"
                >
                  <RefreshCcw size={12} /> Random
                </button>
              </div>
              {errors.pin && <p className="mt-1 text-[11px] text-napoli-oxblood font-body">{errors.pin}</p>}
            </label>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="staff-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="staff-submit"
              onClick={submit}
              disabled={submitting}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : isEdit ? "Save" : "Add staff"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --------------- Reset PIN modal ---------------
const ResetPinModal = ({ open, onClose, staff, onSaved }) => {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPin("");
      setSubmitting(false);
    }
  }, [open]);

  const valid = /^\d{4}$/.test(pin);
  const submit = async () => {
    if (!valid || !staff) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/staff/${staff.id}/reset-pin`, { new_pin: pin });
      toast.success(`PIN reset. Share ${pin} with ${staff.first_name} securely.`);
      onSaved();
      onClose();
    } catch {
      toast.error("Couldn't reset PIN.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="reset-pin-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Reset PIN
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            New 4-digit PIN for <span className="font-semibold not-italic text-napoli-espresso">{[staff?.first_name, staff?.last_name].filter(Boolean).join(" ")}</span>. Share it with them securely.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              data-testid="reset-pin-input"
              type="text"
              inputMode="numeric"
              value={pin}
              maxLength={4}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
              placeholder="0000"
              className="flex-1 bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-2.5 font-mono text-[24px] tracking-[0.5em] text-napoli-espresso text-center focus:outline-none focus:border-napoli-oxblood transition-colors"
            />
            <button
              type="button"
              data-testid="reset-pin-random"
              onClick={() => setPin(randomPin(4))}
              className="px-3 py-2.5 border border-napoli-gold/40 bg-napoli-cream-warm text-napoli-espresso font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:border-napoli-oxblood/40 inline-flex items-center gap-1.5"
            >
              <RefreshCcw size={12} />
            </button>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="reset-pin-cancel"
              onClick={onClose}
              className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="reset-pin-submit"
              disabled={!valid || submitting}
              onClick={submit}
              className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
            >
              {submitting ? "…" : "Reset"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --------------- Deactivate confirm ---------------
const DeactivateModal = ({ open, onClose, staff, onConfirmed }) => {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!staff) return;
    setSubmitting(true);
    try {
      await api.delete(`/admin/staff/${staff.id}`);
      toast.success(`Deactivated ${staff.first_name}. They can no longer sign in.`);
      onConfirmed();
      onClose();
    } catch {
      toast.error("Couldn't deactivate.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="deactivate-modal"
        className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
            Deactivate {staff?.first_name}?
          </DialogTitle>
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            They'll disappear from the staff PIN-login picker. All past transactions stay intact.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="deactivate-cancel"
            onClick={onClose}
            className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="deactivate-submit"
            disabled={submitting}
            onClick={submit}
            className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
          >
            {submitting ? "…" : "Deactivate"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --------------- Row / Card ---------------
const StaffRow = ({ s, onEdit, onResetPin, onDeactivate }) => (
  <tr
    data-testid={`staff-row-${s.id}`}
    className={`border-b border-napoli-gold/15 last:border-0 hover:bg-napoli-cream-warm/60 transition-colors ${s.active ? "" : "opacity-60"}`}
  >
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-oxblood flex items-center justify-center font-display font-bold text-[13px]">
          {initialsOf(s)}
        </div>
        <div>
          <p className="font-body font-semibold text-[14px] text-napoli-espresso">{s.first_name} {s.last_name}</p>
          <p className="font-body text-[11px] text-napoli-espresso-soft">{s.email}</p>
        </div>
      </div>
    </td>
    <td className="px-4 py-3 font-body text-[12px] text-napoli-espresso-soft">{fmtRelative(s.pin_last_used_at)}</td>
    <td className="px-4 py-3 font-body font-bold text-[14px] text-napoli-gold tabular-nums">{fmtNumber(s.total_points_awarded)}</td>
    <td className="px-4 py-3 font-body text-[13px] text-napoli-espresso tabular-nums">{s.total_customers_served}</td>
    <td className="px-4 py-3">
      <span
        className={`inline-flex items-center px-2 py-1 font-body font-semibold text-[10px] tracking-[0.22em] uppercase border ${
          s.active
            ? "bg-napoli-gold/15 text-napoli-gold border-napoli-gold/50"
            : "bg-napoli-cream-warm text-napoli-espresso-soft border-napoli-espresso-soft/40"
        }`}
      >
        {s.active ? "Active" : "Inactive"}
      </span>
    </td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <button
        type="button"
        data-testid={`staff-edit-${s.id}`}
        onClick={() => onEdit(s)}
        className="inline-flex items-center justify-center w-8 h-8 text-napoli-gold hover:bg-napoli-gold/10 transition-colors mr-1"
        aria-label="Edit"
      >
        <Pencil size={14} />
      </button>
      <button
        type="button"
        data-testid={`staff-reset-pin-${s.id}`}
        onClick={() => onResetPin(s)}
        className="inline-flex items-center justify-center w-8 h-8 text-napoli-oxblood hover:bg-napoli-oxblood/10 transition-colors mr-1"
        aria-label="Reset PIN"
      >
        <KeyRound size={14} />
      </button>
      {s.active ? (
        <button
          type="button"
          data-testid={`staff-deactivate-${s.id}`}
          onClick={() => onDeactivate(s)}
          className="inline-flex items-center justify-center w-8 h-8 text-napoli-oxblood hover:bg-napoli-oxblood/10 transition-colors"
          aria-label="Deactivate"
        >
          <PauseCircle size={14} />
        </button>
      ) : null}
    </td>
  </tr>
);

const StaffCardMobile = ({ s, onEdit, onResetPin, onDeactivate }) => (
  <div
    data-testid={`staff-card-${s.id}`}
    className={`bg-napoli-marble border border-napoli-gold/20 p-4 ${s.active ? "" : "opacity-60"}`}
  >
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-full bg-napoli-cream-warm border border-napoli-gold/40 text-napoli-oxblood flex items-center justify-center font-display font-bold text-[14px] flex-shrink-0">
        {initialsOf(s)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-body font-semibold text-[14px] text-napoli-espresso truncate">{s.first_name} {s.last_name}</p>
            <p className="font-body text-[11px] text-napoli-espresso-soft truncate">{s.email}</p>
          </div>
          <span
            className={`inline-flex items-center px-2 py-0.5 font-body font-semibold text-[9px] tracking-[0.22em] uppercase border whitespace-nowrap ${
              s.active
                ? "bg-napoli-gold/15 text-napoli-gold border-napoli-gold/50"
                : "bg-napoli-cream-warm text-napoli-espresso-soft border-napoli-espresso-soft/40"
            }`}
          >
            {s.active ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-body text-[11px] text-napoli-espresso-soft">
          <span>Last PIN use: {fmtRelative(s.pin_last_used_at)}</span>
          <span>· {fmtNumber(s.total_points_awarded)} pts awarded</span>
          <span>· {s.total_customers_served} served</span>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid={`staff-edit-mobile-${s.id}`}
            onClick={() => onEdit(s)}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-napoli-gold/40 bg-napoli-cream-warm text-napoli-espresso font-body font-semibold text-[10px] tracking-[0.18em] uppercase"
          >
            <Pencil size={11} /> Edit
          </button>
          <button
            type="button"
            data-testid={`staff-reset-pin-mobile-${s.id}`}
            onClick={() => onResetPin(s)}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-napoli-oxblood/40 bg-napoli-cream-warm text-napoli-oxblood font-body font-semibold text-[10px] tracking-[0.18em] uppercase"
          >
            <KeyRound size={11} /> Reset PIN
          </button>
          {s.active ? (
            <button
              type="button"
              data-testid={`staff-deactivate-mobile-${s.id}`}
              onClick={() => onDeactivate(s)}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-napoli-oxblood/40 text-napoli-oxblood font-body font-semibold text-[10px] tracking-[0.18em] uppercase"
            >
              <PauseCircle size={11} /> Off
            </button>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

const AdminStaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [resetPinStaff, setResetPinStaff] = useState(null);
  const [deactivateStaff, setDeactivateStaff] = useState(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(data.staff || []);
    } catch {
      setErr("Couldn't load staff.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const activeCount = staff.filter((s) => s.active).length;

  return (
    <section data-testid="admin-staff-page" className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
            Staff &amp; access
          </p>
          <h1 className="mt-2 font-display font-bold text-[28px] sm:text-[32px] text-napoli-espresso leading-tight">
            Manage your team.
          </h1>
          <p className="mt-1 font-body italic text-[14px] text-napoli-espresso-soft">
            {activeCount} active staff · {staff.length - activeCount} deactivated.
          </p>
        </div>
        <button
          type="button"
          data-testid="staff-new"
          onClick={() => { setEditingStaff(null); setModalOpen(true); }}
          className="inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors self-start"
        >
          <Plus size={14} /> Add staff
        </button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="staff-mobile">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-napoli-marble animate-pulse" />)
        ) : err ? (
          <p className="font-body italic text-napoli-oxblood text-center py-8">{err}</p>
        ) : staff.length === 0 ? (
          <div className="bg-napoli-marble border border-napoli-gold/20 p-6 text-center font-body italic text-[13px] text-napoli-espresso-soft">
            No staff yet. Tap "Add staff" above.
          </div>
        ) : (
          staff.map((s) => (
            <StaffCardMobile
              key={s.id}
              s={s}
              onEdit={(x) => { setEditingStaff(x); setModalOpen(true); }}
              onResetPin={setResetPinStaff}
              onDeactivate={setDeactivateStaff}
            />
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-napoli-marble border border-napoli-gold/20">
        <table className="w-full" data-testid="staff-table">
          <thead>
            <tr className="border-b border-napoli-gold/30">
              {["Staff", "Last PIN use", "Awarded", "Served", "Status", ""].map((h, i) => (
                <th key={i} className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-napoli-gold/15">
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-4 bg-napoli-cream-warm animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : err ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-body italic text-napoli-oxblood">{err}</td></tr>
            ) : staff.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center font-body italic text-[13px] text-napoli-espresso-soft">No staff yet.</td></tr>
            ) : (
              staff.map((s) => (
                <StaffRow
                  key={s.id}
                  s={s}
                  onEdit={(x) => { setEditingStaff(x); setModalOpen(true); }}
                  onResetPin={setResetPinStaff}
                  onDeactivate={setDeactivateStaff}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <StaffModal open={modalOpen} onClose={() => setModalOpen(false)} initial={editingStaff} onSaved={reload} />
      <ResetPinModal open={!!resetPinStaff} onClose={() => setResetPinStaff(null)} staff={resetPinStaff} onSaved={reload} />
      <DeactivateModal open={!!deactivateStaff} onClose={() => setDeactivateStaff(null)} staff={deactivateStaff} onConfirmed={reload} />
    </section>
  );
};

export default AdminStaffPage;
